import { useState, useEffect, useMemo } from "react";
import Select from "components/react-select";
import { components, OptionProps, GroupTypeBase } from "react-select";
import { Button } from "components/button";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import Switch from "rc-switch";
import AssigneeSelect from "components/react-select/assignees";
import { Input } from "components/input";
import { useSnackbarContext } from "components/snackbar";
import { useTranslation } from 'react-i18next';

type mutateProps = {
  userId: number | string;
  routeId: string | number;
};
type Options = {
  value: number;
  label: string;
};
const PermissionsIndex = () => {
  const [userID, setUserID] = useState<number | null>(null);
  const [templateID, setTemplateID] = useState<string | null | string>(null);

  const { showSnackbar } = useSnackbarContext();
  const { t, i18n } = useTranslation();
  const tBase = "views.admin.permissions.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const [templateName, setTemplateName] =
    useState<string>("Templates...");
  const [userName, setUserName] = useState<string>("Users...");
  const [permissionArray, setPermissionArray] = useState<any[]>([]);
  const [originalPermissionArray, setOriginalPermissionArray] = useState<any[]>([]);
  const [newTemplateName, setNewTemplateName] = useState("");


  const queryClient = useQueryClient();


  const getSendType = () => {
    if (templateID === null && userID !== null) {

      return "user";
    } else {

      return "template";
    }
  };

  const pTemplates = useQuery(
    ["ptQuery"],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/permissions/permission/templates`
        )
        .then((res) => res.data)
  );

  const permissions = useQuery(
    ["permissions", userID, templateID, i18n.language],
    async () => {
      const id = getSendType() === "user" ? userID : templateID;

      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/permissions/${id}?type=${getSendType()}`
      );
      res.data.forEach((route: any) => {
        route.name = t(`views.admin.permissions.index.${route.name}`, {
          defaultValue: route.name,
          returnObjects: true,
        });
      });

      setPermissionArray(
        res.data.map((a: any) => ({ id: a.id, allowed: a.allowed }))
      );
      
      return res.data;
    },
    {
      keepPreviousData: true, // <--- this is key
    }
  );
  

  
  
  const toggleRoute = useMutation(
    ["togglePermissions"],
    async (props: mutateProps) => {
      await axios.post(
        `${
          process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/permissions?type=${getSendType()}`,
        {
          userId: props.userId,
          routeId: props.routeId,
        }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("permissions");
      },
    }
  );

  const saveTemplate = useMutation(
    ["saveTemplate"],
    async (props: any) => {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/permissions/saveTemplate`,
        props
      );
      return res.data; // returns { id, name }
    }
  );

  const mappedPermissions = useMemo(() => {
    return permissions.data?.map((route: any) => ({
      id: route.id,
      allowed: route.allowed,
    })) ?? [];
  }, [permissions.data]);
  
  useEffect(() => {
    // Do NOT update permissionArray if user is switching to create a new template
    if (templateID !== ':id') {
      setPermissionArray(mappedPermissions);
      setNewTemplateName("");
      if (templateID == null) {
        setOriginalPermissionArray(mappedPermissions);
      }
    }
  }, [mappedPermissions, templateID]);
  

  
  const hasUnsavedChanges = () => {
    return permissionArray.some((curr) => {
      const original = originalPermissionArray.find((o) => o.id === curr.id);
      return original && original.allowed !== curr.allowed;
    });
  };
  
  const pTemplateOptions = ({
    children,
    ...props
  }: OptionProps<Options, true, GroupTypeBase<Options>>) => (
    <components.Option {...props}>
      <div>{children}</div>
    </components.Option>
  );
  const writtenList: any = [];
  const templatesList = useMemo(() => {
    const writtenSet = new Set();
    return (
      pTemplates.data
        ?.filter((a: any) => {
          if (writtenSet.has(a.gid)) return false;
          writtenSet.add(a.gid);
          return true;
        })
        .map((a: any) => a.gid) || []
    );
  }, [pTemplates.data]);
      

  return (
    <div>
      <h1>{tr("Permissions")}</h1>
      <div className="mb-4">
        <p className="py-2">{tr("Select a User or Template to view and edit permissions")}</p>
        <div className="flex flex-row flex-wrap w-1/2 md:w-auto">
          <AssigneeSelect
            className="w-[33%] min-w-[200px] mr-4"
            value={userID}
            placeholder={userName}
            onChange={(e: any) => {
              if (e?.length > 0) {
                setUserID(e[0]?.value);
                setUserName(e[0]?.label);
                setTemplateID(null);
                setTemplateName("Templates...");
              } else {
                return;
              }
            }}
          />

          <Select
            placeholder={templateName}
            // style={{width:'33%', margin:'auto'}}
            className="w-[33%] min-w-[300px] mr-4"
            onChange={(e: any) => {
              if (e !== null && e !== undefined) {
                setTemplateID(e?.value);
                setTemplateName(e?.label);
              } else {
                return;
              }
            }}
            value={templateID}
            options={
              userID ?
              [
              { label: tr("+ New Template"), value: ":id" },
              ...templatesList?.map((a: any) => {
                return {
                  label: pTemplates.data.find((b: any) => {
                    return b.gid === a;
                  }).templatename,
                  value: a,
                };
              }),
            ]
            : []
          }
            components={{ pTemplateOptions }}
          />

          {templateID === ":id" && (
            <Input
              className="w-[250px]"
              placeholder={tr("Enter new template name")}
              value={newTemplateName}
              onChange={(e:any) => setNewTemplateName(e.target.value)}
            />
          )}

          {newTemplateName.length > 0 && templateID === ":id" && (
            <Button 
              className="ml-4"
              primary
              style={{ float: "right" }}
              onClick={async () => {
                const response = await saveTemplate.mutateAsync({
                  routes: permissionArray,
                  template_name: newTemplateName,
                });
                
                if (response?.id && response?.name) {
                  setTemplateID(response.id);
                  setTemplateName(response.name);
                  setNewTemplateName("");
                  showSnackbar(tr("Permissions Template Saved"));
                  await queryClient.invalidateQueries("ptQuery");
                }
              }}
            >
              {tr("Save Template")}
            </Button>
          )}

          {hasUnsavedChanges() && templateID !== ':id' && (
            <Button
              className="ml-4"
              style={{ float: "right" }}
              onClick={() => {
                setPermissionArray(originalPermissionArray);
                setTemplateID(null);
                setTemplateName("Templates...");
              }}
            >
              {tr("Revert Permission Changes")}
            </Button>
          )}
        </div>
      </div>


      {/* Permission Switches */}
      <div className="flex flex-col w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {permissions.data?.map((route: any) => (
            <div className="flex flex-row items-center border-b-2 border-gray-300 pb-2">
              <Switch
                className="min-w-[44px]" // 44px is the width of the switch
                checked={permissionArray.find((a) => a.id === route.id)?.allowed ?? false}
                disabled={userID === null}
                onChange={(e) => {
                  setPermissionArray(
                    permissionArray.map((a) => {
                      if (a.id === route.id) {
                        return { id: route.id, allowed: e.valueOf() };
                      } else {
                        return a;
                      }
                    })
                  );
                }}
              ></Switch>
              {/* <h4 className="ml-2">{t(`views.admin.permissions.index.${route.name}`, { defaultValue: route.name, returnObjects: true })}</h4> */}
              <h4 className="ml-2">{route.name}</h4>
            </div>
          ))}
        </div>
      </div>

      <div className="py-2"></div>
      <Button
        primary
        className="p-4 m-4"
        disabled={!hasUnsavedChanges() || templateID === ":id"}
        onClick={async () => {
          const changedRoutes = permissionArray.filter((route:any) => {
            const original = originalPermissionArray.find((a:any) => a.id === route.id);
            return !original || original.allowed !== route.allowed;
          });

          for (const route of changedRoutes) {
            if (userID !== null) {
              await toggleRoute.mutateAsync({ userId: userID, routeId: route.id });
            }
          }

          setTemplateID(null);

          queryClient.invalidateQueries("permissions");
          showSnackbar(tr("Permissions Saved"));
        }}
      >
        {tr("Apply")}
      </Button>

    </div>
  );
};

export { PermissionsIndex };
